/**
 * Presets for company primary theme colors
 */
export const COMPANY_PRIMARY_COLOR_PRESETS = [
  { label: "Amber (Default)", value: "#f59e0b" },
  { label: "Emerald Green", value: "#10b981" },
  { label: "Royal Blue", value: "#3b82f6" },
  { label: "Indigo", value: "#6366f1" },
  { label: "Violet / Purple", value: "#8b5cf6" },
  { label: "Rose Red", value: "#f43f5e" },
  { label: "Cyan", value: "#06b6d4" },
  { label: "Slate / Dark", value: "#475569" },
] as const;

/**
 * Calculates relative luminance of hex color to pick light or dark foreground text
 */
export function getContrastForeground(hex: string): string {
  let c = hex.replace("#", "").trim();
  if (c.length === 3) c = c.split("").map((x) => x + x).join("");
  if (c.length !== 6) return "220 20% 7%";
  const r = parseInt(c.slice(0, 2), 16) / 255;
  const g = parseInt(c.slice(2, 4), 16) / 255;
  const b = parseInt(c.slice(4, 6), 16) / 255;

  const lum = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  return lum > 0.65 ? "220 20% 7%" : "0 0% 100%";
}

/**
 * Converts Hex string (#ffffff or #fff) into HSL channel string (e.g. "38 92% 48%")
 */
export function hexToHslChannels(hex: string): string | null {
  let c = hex.replace("#", "").trim();
  if (c.length === 3) {
    c = c.split("").map((x) => x + x).join("");
  }
  if (c.length !== 6) return null;
  const num = parseInt(c, 16);
  if (isNaN(num)) return null;
  let r = ((num >> 16) & 255) / 255;
  let g = ((num >> 8) & 255) / 255;
  let b = (num & 255) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      case b:
        h = (r - g) / d + 4;
        break;
    }
    h /= 6;
  }

  const hDeg = Math.round(h * 360);
  const sPct = Math.round(s * 100);
  const lPct = Math.round(l * 100);

  return `${hDeg} ${sPct}% ${lPct}%`;
}

/**
 * Dynamically applies the given primary color (hex or HSL channels) to CSS variables on :root
 */
export function applyPrimaryColorToDom(colorStr: string | null | undefined) {
  if (typeof document === "undefined") return;

  const root = document.documentElement;

  if (!colorStr || !colorStr.trim()) {
    // Reset to default theme CSS variables
    root.style.removeProperty("--primary");
    root.style.removeProperty("--primary-foreground");
    root.style.removeProperty("--ring");
    root.style.removeProperty("--accent");
    root.style.removeProperty("--accent-foreground");
    root.style.removeProperty("--sidebar-primary");
    root.style.removeProperty("--sidebar-primary-foreground");
    root.style.removeProperty("--sidebar-ring");
    root.style.removeProperty("--gradient-primary");
    root.style.removeProperty("--shadow-glow");
    return;
  }

  const trimmed = colorStr.trim();
  let hslValue = "";

  if (trimmed.startsWith("#")) {
    hslValue = hexToHslChannels(trimmed) ?? "";
  } else if (trimmed.includes("%") || /^\d+\s+\d+/.test(trimmed)) {
    hslValue = trimmed.replace(/hsl\(|\)/g, "");
  }

  if (!hslValue) return;

  const foregroundText = trimmed.startsWith("#") ? getContrastForeground(trimmed) : "0 0% 100%";

  root.style.setProperty("--primary", hslValue);
  root.style.setProperty("--primary-foreground", foregroundText);
  root.style.setProperty("--ring", hslValue);
  root.style.setProperty("--accent", hslValue);
  root.style.setProperty("--accent-foreground", foregroundText);
  root.style.setProperty("--sidebar-primary", hslValue);
  root.style.setProperty("--sidebar-primary-foreground", foregroundText);
  root.style.setProperty("--sidebar-ring", hslValue);
  root.style.setProperty(
    "--gradient-primary",
    `linear-gradient(135deg, hsl(${hslValue}), hsl(${hslValue}))`
  );
  root.style.setProperty(
    "--shadow-glow",
    `0 0 30px -5px hsl(${hslValue} / 0.25)`
  );
}
